import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { switchMap, map, catchError, of, forkJoin } from "rxjs";
import {
    CompanyClient,
    LocationClient,
    SalesPersonClient,
    SalesSupportPersonClient,
    UserClient,
} from "src/api-service";
import { normalizeError } from "../../../../../app-store-error.types";
import { InternalUsersActions } from "./internal-users.actions";

@Injectable()
export class InternalUsersEffects {
    private readonly actions$ = inject(Actions);
    private readonly userClient = inject(UserClient);
    private readonly companyClient = inject(CompanyClient);
    private readonly locationClient = inject(LocationClient);
    private readonly salesPersonClient = inject(SalesPersonClient);
    private readonly salesSupportPersonClient = inject(SalesSupportPersonClient);

    // =========================================================================
    // USERS
    // =========================================================================

    loadUsers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(InternalUsersActions.loadUsers),
            switchMap(() =>
                this.userClient.getAll().pipe(
                    map(users => InternalUsersActions.loadUsersSuccess({ users: users ?? [] })),
                    catchError(error =>
                        of(
                            InternalUsersActions.loadUsersFailure({
                                error: normalizeError(error),
                                toastKey: "ADMIN_PANEL.EDIT_USERS.INTERNAL.TOAST.LOAD_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    createUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(InternalUsersActions.createUser),
            switchMap(({ user }) =>
                this.userClient.createUser(user).pipe(
                    map(() =>
                        // API returns User, not AdminUser - use input data
                        InternalUsersActions.createUserSuccess({
                            user,
                            toastKey: "ADMIN_PANEL.EDIT_USERS.INTERNAL.TOAST.CREATE_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(
                            InternalUsersActions.createUserFailure({
                                error: normalizeError(error),
                                toastKey: "ADMIN_PANEL.EDIT_USERS.INTERNAL.TOAST.CREATE_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    updateUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(InternalUsersActions.updateUser),
            switchMap(({ user }) =>
                this.userClient.updateUser(user).pipe(
                    map(() =>
                        // API returns User, not AdminUser - use input data
                        InternalUsersActions.updateUserSuccess({
                            user,
                            toastKey: "ADMIN_PANEL.EDIT_USERS.INTERNAL.TOAST.UPDATE_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(
                            InternalUsersActions.updateUserFailure({
                                error: normalizeError(error),
                                toastKey: "ADMIN_PANEL.EDIT_USERS.INTERNAL.TOAST.UPDATE_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    deleteUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(InternalUsersActions.deleteUser),
            switchMap(({ email }) =>
                this.userClient.deleteUser(email).pipe(
                    map(() =>
                        InternalUsersActions.deleteUserSuccess({
                            email,
                            toastKey: "ADMIN_PANEL.EDIT_USERS.INTERNAL.TOAST.DELETE_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(
                            InternalUsersActions.deleteUserFailure({
                                error: normalizeError(error),
                                toastKey: "ADMIN_PANEL.EDIT_USERS.INTERNAL.TOAST.DELETE_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    // =========================================================================
    // COMPANIES
    // =========================================================================

    loadCompanies$ = createEffect(() =>
        this.actions$.pipe(
            ofType(InternalUsersActions.loadCompanies),
            switchMap(() =>
                this.companyClient.getAll().pipe(
                    map(companies =>
                        InternalUsersActions.loadCompaniesSuccess({ companies: companies ?? [] }),
                    ),
                    catchError(error =>
                        of(
                            InternalUsersActions.loadCompaniesFailure({
                                error: normalizeError(error),
                                toastKey: "ADMIN_PANEL.EDIT_USERS.INTERNAL.COMPANIES_LOAD_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    // =========================================================================
    // SALES DATA
    // =========================================================================

    loadSalesData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(InternalUsersActions.loadSalesData),
            switchMap(({ companyId }) =>
                forkJoin({
                    salesPersons: this.salesPersonClient.getAll(companyId).pipe(
                        catchError(() => of([])),
                    ),
                    salesSupport: this.salesSupportPersonClient.getAll(companyId).pipe(
                        catchError(() => of([])),
                    ),
                }).pipe(
                    map(({ salesPersons, salesSupport }) =>
                        InternalUsersActions.loadSalesDataSuccess({
                            salesPersons: salesPersons ?? [],
                            salesSupport: salesSupport ?? [],
                        }),
                    ),
                    catchError(error =>
                        of(
                            InternalUsersActions.loadSalesDataFailure({
                                error: normalizeError(error),
                                toastKey: "ADMIN_PANEL.EDIT_USERS.INTERNAL.TOAST.SALES_DATA_LOAD_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    // =========================================================================
    // SALES PERSON
    // =========================================================================

    saveSalesPerson$ = createEffect(() =>
        this.actions$.pipe(
            ofType(InternalUsersActions.saveSalesPerson),
            switchMap(({ salesPerson, companyId }) =>
                this.salesPersonClient.addOrUpdate(salesPerson, companyId).pipe(
                    map(() =>
                        // API returns SalesPerson, not AdminSalesPerson - use input data
                        InternalUsersActions.saveSalesPersonSuccess({
                            salesPerson,
                            toastKey: "ADMIN_PANEL.EDIT_USERS.INTERNAL.TOAST.SALES_PERSON_SAVE_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(
                            InternalUsersActions.saveSalesPersonFailure({
                                error: normalizeError(error),
                                toastKey: "ADMIN_PANEL.EDIT_USERS.INTERNAL.TOAST.SALES_PERSON_SAVE_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    deleteSalesPerson$ = createEffect(() =>
        this.actions$.pipe(
            ofType(InternalUsersActions.deleteSalesPerson),
            switchMap(({ email, companyId }) =>
                this.salesPersonClient.delete(email, companyId).pipe(
                    map(() =>
                        InternalUsersActions.deleteSalesPersonSuccess({
                            email,
                            toastKey: "ADMIN_PANEL.EDIT_USERS.INTERNAL.TOAST.SALES_PERSON_DELETE_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(
                            InternalUsersActions.deleteSalesPersonFailure({
                                error: normalizeError(error),
                                toastKey: "ADMIN_PANEL.EDIT_USERS.INTERNAL.TOAST.SALES_PERSON_DELETE_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    // =========================================================================
    // SALES SUPPORT
    // =========================================================================

    saveSalesSupport$ = createEffect(() =>
        this.actions$.pipe(
            ofType(InternalUsersActions.saveSalesSupport),
            switchMap(({ salesSupport, companyId }) =>
                this.salesSupportPersonClient.addOrUpdate(salesSupport, companyId).pipe(
                    map(() =>
                        // API returns SalesSupportPerson, not AdminSalesSupportPerson - use input data
                        InternalUsersActions.saveSalesSupportSuccess({
                            salesSupport,
                            toastKey: "ADMIN_PANEL.EDIT_USERS.INTERNAL.TOAST.SALES_SUPPORT_SAVE_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(
                            InternalUsersActions.saveSalesSupportFailure({
                                error: normalizeError(error),
                                toastKey: "ADMIN_PANEL.EDIT_USERS.INTERNAL.TOAST.SALES_SUPPORT_SAVE_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    deleteSalesSupport$ = createEffect(() =>
        this.actions$.pipe(
            ofType(InternalUsersActions.deleteSalesSupport),
            switchMap(({ email, companyId }) =>
                this.salesSupportPersonClient.delete(email, companyId).pipe(
                    map(() =>
                        InternalUsersActions.deleteSalesSupportSuccess({
                            email,
                            toastKey: "ADMIN_PANEL.EDIT_USERS.INTERNAL.TOAST.SALES_SUPPORT_DELETE_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(
                            InternalUsersActions.deleteSalesSupportFailure({
                                error: normalizeError(error),
                                toastKey: "ADMIN_PANEL.EDIT_USERS.INTERNAL.TOAST.SALES_SUPPORT_DELETE_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    // =========================================================================
    // LOCATIONS
    // =========================================================================

    loadLocations$ = createEffect(() =>
        this.actions$.pipe(
            ofType(InternalUsersActions.loadLocations),
            switchMap(({ companyId }) =>
                this.locationClient.getAllLocations(companyId, 1, 1000).pipe(
                    map(response =>
                        InternalUsersActions.loadLocationsSuccess({
                            locations: response.data ?? [],
                        }),
                    ),
                    catchError(error =>
                        of(
                            InternalUsersActions.loadLocationsFailure({
                                error: normalizeError(error),
                                toastKey: "ADMIN_PANEL.EDIT_USERS.INTERNAL.TOAST.LOCATIONS_LOAD_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );
}
